import { apiClient } from "@/lib/api/client";

export type ReportRecordDto = {
  id: string;
  tenant_id: string | null;
  device_id: string | null;
  device_name: string | null;
  name: string;
  description: string | null;
  report_type: string;
  channel_keys: string[];
  analysis_window: string;
  frequency: string;
  export_format: string;
  recipient_emails: string[];
  status: string;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string;
  updated_at: string;
};

export type ReportRunDto = {
  id: string;
  report_id: string;
  tenant_id: string | null;
  status: string;
  export_format: string;
  file_name: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  generated_at: string | null;
  emailed_at: string | null;
  error_message: string | null;
  created_at: string;
};

export type GeneratedReportDownload = {
  blob: Blob;
  fileName: string;
};

export type ReportsListResponse = {
  items: ReportRecordDto[];
  total: number;
};

export type ReportCreateBody = {
  tenant_id?: string | null;
  name: string;
  description?: string | null;
  report_type: string;
  device_id: string;
  channel_keys: string[];
  analysis_window: string;
  frequency: string;
  export_format: string;
  recipient_emails?: string[];
  status?: string;
  last_run_at?: string | null;
  next_run_at?: string | null;
};

export type ReportUpdateBody = Partial<ReportCreateBody>;

export async function listReports(tenantId?: string | null): Promise<ReportsListResponse> {
  const { data } = await apiClient.get<ReportsListResponse>("/reports", {
    params: tenantId ? { tenant_id: tenantId } : undefined,
  });
  return data;
}

export async function createReport(body: ReportCreateBody): Promise<ReportRecordDto> {
  const { data } = await apiClient.post<ReportRecordDto>("/reports", body);
  return data;
}

export async function updateReport(reportId: string, body: ReportUpdateBody): Promise<ReportRecordDto> {
  const { data } = await apiClient.put<ReportRecordDto>(`/reports/${reportId}`, body);
  return data;
}

export async function deleteReport(reportId: string): Promise<void> {
  await apiClient.delete(`/reports/${reportId}`);
}

function parseDownloadFilename(disposition: string | undefined, fallback: string): string {
  if (!disposition) {
    return fallback;
  }
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const simpleMatch = disposition.match(/filename=\"?([^\";]+)\"?/i);
  return simpleMatch?.[1] || fallback;
}

export async function generateReport(reportId: string, fallbackFileName = "report.bin"): Promise<GeneratedReportDownload> {
  const response = await apiClient.post<Blob>(`/reports/${reportId}/generate`, undefined, {
    responseType: "blob",
  });
  const disposition =
    typeof response.headers["content-disposition"] === "string" ? response.headers["content-disposition"] : undefined;
  return {
    blob: response.data,
    fileName: parseDownloadFilename(disposition, fallbackFileName),
  };
}

export async function downloadReportRun(reportId: string, runId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/reports/${reportId}/runs/${runId}/download`, {
    responseType: "blob",
  });
  return data;
}
